import { AfterViewChecked, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatCard, MatCardContent, MatCardImage } from '@angular/material/card';
import { ResponsiveService } from '../../services/responsive.service';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { Organization } from '../../model/organization/organization.model';
import { Subscription } from 'rxjs';
import { AppStateStore } from '../../store/app-state.store';
import { GoalColor, getGoalColor } from '../../util/goal-color.util';

@Component({
  selector: 'app-organization-list',
  imports: [
    AsyncPipe,
    MatCard,
    MatCardContent,
    MatCardImage,
    NgClass,
    RouterLink,
    MatIcon,
    MatChip,
    MatChipSet,
  ],
  templateUrl: './organization-list.html',
  styleUrl: './organization-list.scss',
})
export class OrganizationList implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('sentinel', {read: ElementRef}) sentinel?: ElementRef;

  public readonly appStateStore = inject(AppStateStore);
  public responsiveService = inject(ResponsiveService);

  public goalColor(goal: { class: string }): GoalColor {
    return getGoalColor(goal.class);
  }

  // Lazy loading properties
  private allOrganizations: Organization[] = [];
  public displayedOrganizations: Organization[] = [];
  private readonly BATCH_SIZE = 25;
  private currentIndex = 0;
  private observer?: IntersectionObserver;
  private organizationListSubscription?: Subscription;
  private isObserving = false;

  ngOnInit(): void {
    // Subscribe to the full organization list (loaded from the stub for now)
    this.organizationListSubscription = this.appStateStore.organizationList$.subscribe((organizations: Organization[]) => {
      this.allOrganizations = organizations;
      // Reset and load initial batch
      this.currentIndex = 0;
      this.displayedOrganizations = [];
      this.isObserving = false;
      this.loadMoreOrganizations();
    });

    // Set up Intersection Observer once
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.hasMoreOrganizations) {
            this.loadMoreOrganizations();
          }
        });
      },
      {
        root: null,
        rootMargin: '100px', // Start loading 100px before reaching the sentinel
        threshold: 0.1,
      },
    );
  }

  ngAfterViewChecked(): void {
    // Observe the sentinel element when it becomes available
    if (this.sentinel?.nativeElement && !this.isObserving && this.hasMoreOrganizations) {
      this.observer?.observe(this.sentinel.nativeElement);
      this.isObserving = true;
    } else if (!this.hasMoreOrganizations && this.isObserving) {
      // Stop observing when there are no more organizations
      this.observer?.disconnect();
      this.isObserving = false;
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.organizationListSubscription?.unsubscribe();
  }

  private loadMoreOrganizations(): void {
    const nextBatch = this.allOrganizations.slice(
      this.currentIndex,
      this.currentIndex + this.BATCH_SIZE,
    );

    if (nextBatch.length > 0) {
      this.displayedOrganizations = [...this.displayedOrganizations, ...nextBatch];
      this.currentIndex += nextBatch.length;
    }
  }

  public get hasMoreOrganizations(): boolean {
    return this.currentIndex < this.allOrganizations.length;
  }
}
